import React from 'react';

import { useGraphQLSuspenseQuery } from '@betterment/graphql';
import { Box, Button, Card, H2, Shimmer, Table, Text, Tooltip } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { nanoid } from 'nanoid';

import BillingPlanIndexPageRow from './BillingPlanIndexPageRow';
import BillingPlanTableHeaderCell from './BillingPlanTableHeaderCell';

import { BillingPlan } from '../../../../graphql/schema';
import { IdempotencyProviderWithFeatureRegistry } from '../../../../providers/IdempotencyProviderWithFeatureRegistry';
import urlBuilder from '../../../../Shared/url-builder';
import { Layout } from '../../../InstitutionalApp/Layout';
import { ClientBillingHeader } from '../../ClientBillingHeader';
import { CashFirstBillingPromptCard } from '../cash-first-billing-prompt-card/CashFirstBillingPromptCard';
import { BillingPlanTableColumn } from '../data';
import { FirmDocument } from '../graphql/query.firm.generated';

const BillingPlanTableHeader = () => (
  <Box css={{ mb: 'space.200', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <H2 variant="title200">Billing plans</H2>
    <SambaAutoLink to={urlBuilder.getBillingPlanFlowPage({ id: nanoid() })}>
      <Button variant="solidPrimary">Create plan</Button>
    </SambaAutoLink>
  </Box>
);

const BillingPlanIndex = () => {
  const {
    data: {
      firm: { billingPlans, platformFee },
    },
  } = useGraphQLSuspenseQuery(FirmDocument);

  const defaultBillingPlan = billingPlans?.filter(plan => plan.firmDefault)[0];
  const billFromColumnName = 'Bill from';

  const paidByTooltip = (
    <Tooltip>
      <Tooltip.Trigger asChild={true} data-testid="platformFeeTooltip">
        <Tooltip.IconButton name="InfoCircle" css={{ ml: 'space.50' }} />
      </Tooltip.Trigger>
      <Tooltip.Content align="center" side="top">
        <Text>Betterment platform fee is {platformFee} bps.</Text>
      </Tooltip.Content>
    </Tooltip>
  );

  const billingPlanTableColumns: BillingPlanTableColumn[] = [
    { name: 'Name' },
    { name: 'Advisory fee' },
    { name: billFromColumnName },
    { name: 'Assigned to' },
    { name: 'Platform fee paid by', tooltip: paidByTooltip },
    { name: '' },
  ];

  return (
    <Box>
      {billingPlans?.length ? <CashFirstBillingPromptCard billingPlans={billingPlans} /> : null}
      <Card css={{ p: 'space.300' }}>
        <Box css={{ overflowX: 'auto' }}>
          <BillingPlanTableHeader />
          <Table
            aria-label="billingPlanTable"
            css={{
              bg: 'background.surface',
              overflowX: 'visible',
              borderTopStyle: 'solid',
              borderTopColor: 'border.weak',
              borderTopWidth: 'borderWidths.thin',
            }}
          >
            <Table.Header>
              <Table.Row>
                {billingPlanTableColumns.map(col => (
                  <BillingPlanTableHeaderCell key={col.name} column={col} tooltip={col.tooltip} />
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <IdempotencyProviderWithFeatureRegistry featureName="billingPlans">
                {billingPlans?.map((plan: BillingPlan, index: number) => (
                  <BillingPlanIndexPageRow
                    key={plan.id}
                    plan={plan}
                    defaultBillingPlan={defaultBillingPlan}
                    platformFee={platformFee}
                    isLastIndex={index === billingPlans.length - 1}
                  />
                ))}
              </IdempotencyProviderWithFeatureRegistry>
              {billingPlans?.length ? null : (
                <Table.Row>
                  <Table.Cell aria-label="emptyTableView" colSpan={6} css={{ textWrap: 'nowrap' }}>
                    <Box css={{ p: 'space.800', textAlign: 'center' }}>
                      <Text as="p" css={{ p: 'space.400', color: 'primitives.grey.50' }}>
                        Once created, you'll see your firm's billing plan here.
                      </Text>
                    </Box>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </Box>
      </Card>
    </Box>
  );
};

export const BillingPlanIndexPage = () => {
  return (
    <Layout active="billing_plans">
      <Layout.Header>
        <ClientBillingHeader />
      </Layout.Header>
      <Layout.Content>
        <React.Suspense
          fallback={
            <Card css={{ p: 'space.300' }}>
              <Box css={{ overflowX: 'auto' }}>
                <BillingPlanTableHeader />
                <Box
                  css={{
                    borderTopStyle: 'solid',
                    borderTopColor: 'border.weak',
                    borderTopWidth: 'borderWidths.thin',
                  }}
                >
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Box
                      /* eslint-disable-next-line react/no-array-index-key */
                      key={i}
                      css={{
                        display: 'flex',
                        gap: 'space.300',
                        py: 'space.300',
                        borderBottomStyle: 'solid',
                        borderBottomColor: 'border.weak',
                        borderBottomWidth: 'borderWidths.thin',
                      }}
                    >
                      {['30%', '15%', '15%', '15%', '15%'].map((width, j) => (
                        <Shimmer
                          /* eslint-disable-next-line react/no-array-index-key */
                          key={j}
                          css={{ height: 'size.200', width, borderRadius: 'radius.100' }}
                        />
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Card>
          }
        >
          <BillingPlanIndex />
        </React.Suspense>
      </Layout.Content>
    </Layout>
  );
};
